class FitTextOverlay extends HTMLElement {
  constructor() {
    super();
    this.resizeObserver = null;
    this.mutationObserver = null;
    this.boundHandleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    // Defer to ensure child content exists
    requestAnimationFrame(() => {
      this.init();
    });
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    window.removeEventListener('resize', this.boundHandleResize);
  }

  init() {
    this.target = this.querySelector('[data-fit-text]') || this.firstElementChild;
    if (!this.target) return;

    const gapPercent = parseFloat(this.dataset.gap || '5');
    this.minFont = parseFloat(this.dataset.minFont || '16');
    this.maxFont = parseFloat(this.dataset.maxFont || '300');
    this.overlapPercent = parseFloat(this.dataset.overlapPercent || '15');
    this.bottomPaddingPx = parseFloat(this.dataset.bottomPad || '40');

    this.gapPx = () => (this.clientWidth * (gapPercent / 100));

    // Prepare fades: container hidden until first layout pass; images fade when loaded
    this.containerEl = this.closest('.fit-hero__container');
    if (this.containerEl) {
      this.containerEl.classList.remove('is-ready');
    }
    this.setupImageFade();

    this.fitNow();
    this.layoutNow();

    const fontsReady = (document.fonts && document.fonts.ready)
      ? document.fonts.ready
      : Promise.resolve();
    fontsReady.then(() => {
      // Re-run with final font metrics, then reveal
      this.fitNow();
      this.layoutNow();
      this.markReady();
    });

    // Observe explicit resizes
    this.resizeObserver = new ResizeObserver(() => { this.fitNow(); this.layoutNow(); });
    this.resizeObserver.observe(this);
    window.addEventListener('resize', this.boundHandleResize, { passive: true });

    // Re-fit after load and fonts are ready
    window.addEventListener('load', () => { this.fitNow(); this.layoutNow(); });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { this.fitNow(); this.layoutNow(); });
    }

    // Re-fit if text content changes
    this.mutationObserver = new MutationObserver(() => { this.fitNow(); this.layoutNow(); });
    this.mutationObserver.observe(this, { childList: true, characterData: true, subtree: true });
  }

  handleResize() {
    this.fitNow();
    this.layoutNow();
  }

  fitNow() {
    const containerWidth = this.clientWidth - (this.gapPx() * 2);
    if (!this.target || containerWidth <= 0) return;

    // Reset to measure properly
    this.target.style.setProperty('white-space', 'nowrap', 'important');
    this.target.style.setProperty('display', 'inline-block', 'important');
    this.target.style.setProperty('margin', '0', 'important');
    this.target.style.setProperty('width', 'auto', 'important');

    // Binary search ideal font size between min and max
    let low = this.minFont;
    let high = this.maxFont;
    let best = low;
    for (let i = 0; i < 12; i++) {
      const mid = (low + high) / 2;
      this.target.style.setProperty('font-size', mid + 'px', 'important');
      const measured = this.target.getBoundingClientRect().width;
      if (measured <= containerWidth) {
        best = mid;
        low = mid;
      } else {
        high = mid;
      }
    }
    this.target.style.setProperty('font-size', Math.min(best, this.maxFont) + 'px', 'important');

    // Center align
    this.style.textAlign = 'center';
  }

  layoutNow() {
    try {
      const container = this.closest('.fit-hero__container');
      if (!container) return;
      const imageWrapper = container.querySelector('.fit-hero__image');
      if (!imageWrapper) return;

      // Compute text height and overlap in px
      const textRect = this.target.getBoundingClientRect();
      const textHeight = textRect.height || 0;
      const overlapPx = Math.max(0, (this.overlapPercent / 100) * textHeight);

      const isNatural = !!container.closest('.fade-hero--natural');

      if (isNatural) {
        // Image renders at its natural aspect ratio; don't pin its height.
        // Just clear any prior inline height and overlap the text into the
        // image's actual bottom edge via translate.
        imageWrapper.style.height = '';
      } else {
        // Section/container height
        const totalHeight = container.getBoundingClientRect().height;
        if (!totalHeight) return;

        // imageHeight + (textHeight - overlapPx) + bottomPadding = totalHeight
        let imageHeight = totalHeight - (textHeight - overlapPx) - this.bottomPaddingPx;
        if (!Number.isFinite(imageHeight)) return;
        imageHeight = Math.max(0, imageHeight);

        imageWrapper.style.height = Math.round(imageHeight) + 'px';
      }

      // Apply translate in px to match requested behavior
      this.style.transform = `translateY(-${Math.round(overlapPx)}px)`;
      // Apply bottom spacing under text
      this.style.marginBottom = Math.round(this.bottomPaddingPx) + 'px';
    } catch (_) {
      // no-op
    }
  }

  setupImageFade() {
    const container = this.closest('.fit-hero__container');
    if (!container) return;
    const imgs = container.querySelectorAll('.fit-hero__image img');
    imgs.forEach((img) => {
      const applyLoaded = () => {
        img.classList.add('loaded');
        // In natural-aspect-ratio mode the image dictates the layout height,
        // so re-run layout once the image's intrinsic dimensions are known.
        this.layoutNow();
      };
      if (img.complete && img.naturalWidth > 0) {
        applyLoaded();
      } else {
        img.addEventListener('load', applyLoaded, { once: true });
        img.addEventListener('error', applyLoaded, { once: true });
      }
    });
  }

  markReady() {
    if (this.containerEl) {
      // Use rAF to ensure styles applied before showing
      requestAnimationFrame(() => {
        this.containerEl.classList.add('is-ready');
      });
    }
  }
}

customElements.define('fit-text-overlay', FitTextOverlay);



